<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";
import General from "@/services/General";
import modalAddEditChangelog from "@/components/modals/dashboard/modalAddEditChangelog";
import Swal from "sweetalert2";

export default {
  components: { Layout, PageHeader, modalAddEditChangelog },
  page: {
    title: "Dashboard Changelog",
    meta: [
      {
        name: "description",
        content: appConfig.description,
      },
    ],
  },
  data() {
    return {
      title: "Dashboard Changelog",
      items: [
        {
          text: "Dashboard",
        },
        {
          text: "Changelog",
          active: true,
        },
      ],
      version: process.env.VUE_APP_VERSION || "Unknown",
      changelogData: [],
      groupedChangelog: {},
      error: null,
      isBusy: false,
      searchQuery: '',
    };
  },
  computed: {
    filteredChangelog() {
      if (!this.groupedChangelog || typeof this.groupedChangelog !== 'object' || Object.keys(this.groupedChangelog).length === 0) {
        return {};
      }
      
      if (!this.searchQuery) return this.groupedChangelog;
      
      const query = this.searchQuery.toLowerCase();
      const filtered = {};
      
      Object.keys(this.groupedChangelog).forEach(version => {
        const versionEntries = this.groupedChangelog[version];
        if (!Array.isArray(versionEntries)) return;
        
        const filteredChanges = versionEntries.filter(entry => {
          if (!entry || typeof entry !== 'object') return false;
          return (
            (entry.version && String(entry.version).toLowerCase().includes(query)) ||
            (entry.change && String(entry.change).toLowerCase().includes(query)) ||
            (entry.ticket && String(entry.ticket).toLowerCase().includes(query)) ||
            (entry.type && String(entry.type).toLowerCase().includes(query))
          );
        });
        
        if (filteredChanges.length > 0) {
          filtered[version] = filteredChanges;
        }
      });
      
      return filtered;
    },
    sortedVersions() {
      if (!this.filteredChangelog || typeof this.filteredChangelog !== 'object' || Object.keys(this.filteredChangelog).length === 0) {
        return [];
      }
      return Object.keys(this.filteredChangelog).sort((a, b) => {
        // Sort versions in descending order (newest first)
        return b.localeCompare(a);
      });
    },
  },
  async mounted() {
    await this.loadChangelog();
  },
  methods: {
    async loadChangelog() {
      this.isBusy = true;
      this.error = null;
      try {
        const response = await General.getDashboardVersion(this.version);
        if (response.data && response.data.data) {
          this.changelogData = response.data.data;
          this.groupByVersion();
        } else if (response.data && response.data.error) {
          this.error = response.data.error;
          this.changelogData = [];
          this.groupedChangelog = {};
        }
      } catch (err) {
        this.error = err.response?.data?.error || err.message || "Failed to load changelog";
        this.changelogData = [];
        this.groupedChangelog = {};
        console.error("Error loading dashboard version:", err);
      } finally {
        this.isBusy = false;
      }
    },
    groupByVersion() {
      this.groupedChangelog = {};
      if (!this.changelogData || !Array.isArray(this.changelogData)) {
        return;
      }
      
      this.changelogData.forEach(entry => {
        if (!entry) return;
        const version = entry.version || 'Unknown';
        if (!this.groupedChangelog[version]) {
          this.groupedChangelog[version] = [];
        }
        this.groupedChangelog[version].push(entry);
      });
      
      // Sort entries within each version by timestamp (newest first)
      Object.keys(this.groupedChangelog).forEach(version => {
        if (this.groupedChangelog[version] && Array.isArray(this.groupedChangelog[version])) {
          this.groupedChangelog[version].sort((a, b) => {
            const dateA = new Date(a?.timestamp || 0);
            const dateB = new Date(b?.timestamp || 0);
            return dateB - dateA;
              });
        }
      });
    },
    formatDate(dateString) {
      if (!dateString) return "N/A";
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      } catch (e) {
        return dateString;
      }
    },
    openAddModal() {
      this.$refs.modalAddEditChangelog.openModal();
    },
    openEditModal(change) {
      if (!change || !change.id) {
        this.failedmsg("Invalid changelog entry: ID is missing");
        return;
      }
      this.$refs.modalAddEditChangelog.openModal(change);
    },
    async deleteChangelog(change) {
      if (!change || !change.id) {
        this.failedmsg("Invalid changelog entry");
        return;
      }

      const result = await Swal.fire({
        title: "Are you sure?",
        text: "This changelog entry will be deleted!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#34c38f",
        cancelButtonColor: "#f46a6a",
        confirmButtonText: "Yes, delete it!",
      });

      if (result.value) {
        try {
          const response = await General.deleteDashboardChangelog(change.id);
          if (response.data && response.data.data) {
            Swal.fire("Deleted!", "Changelog entry has been deleted.", "success");
            await this.loadChangelog();
          } else {
            const error = response.data?.error || "Failed to delete changelog";
            this.failedmsg(error);
          }
        } catch (error) {
          const errorMessage = error.response?.data?.error || error.message || "Failed to delete changelog";
          this.failedmsg(errorMessage);
          console.error("Error deleting changelog:", error);
        }
      }
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <div class="row">
      <div class="col-12 mb-3">
        <button
          v-if="menuToArray.includes('button.changelog.addnewchangelog')"
          class="btn btn-success waves-effect waves-light"
          @click="openAddModal"
        >
          <i class="mdi mdi-plus me-2"></i>
          Add New Changelog
        </button>
      </div>
    </div>

    <div class="row">
            <!-- Error Message -->
            <div v-if="error" class="alert alert-warning mb-4" role="alert">
              <h6 class="alert-heading">
                <i class="uil uil-exclamation-triangle me-2"></i>
                Unable to load changelog
              </h6>
              <p class="mb-0">{{ error }}</p>
            </div>

            <!-- Loading State -->
            <div v-if="isBusy" class="text-center py-5">
              <b-spinner large variant="primary"></b-spinner>
              <p class="mt-3">Loading changelog...</p>
            </div>

            <!-- Changelog Timeline -->
            <div v-else-if="!error && sortedVersions && sortedVersions.length > 0" class="changelog-container">
              <div 
                v-for="versionKey in sortedVersions" 
                :key="versionKey"
                class="version-group mb-4"
              >
                <!-- Version Card with all changes -->
                <div class="card border-start border-3">
                  <div class="card-header bg-light">
                    <div class="d-flex align-items-center">
                      <div class="d-flex align-items-center">
                        <span class="version-badge me-3">{{ versionKey }}</span>
                        <span class="version-count text-muted">
                          {{ filteredChangelog && filteredChangelog[versionKey] ? filteredChangelog[versionKey].length : 0 }} 
                          {{ (filteredChangelog && filteredChangelog[versionKey] ? filteredChangelog[versionKey].length : 0) === 1 ? 'change' : 'changes' }}
                        </span>
                </div>
                      <span v-if="versionKey === version" class="badge bg-success ms-2">
                        <i class="mdi mdi-check-circle me-1"></i>
                        Current
                      </span>
              </div>
            </div>
                  <div class="card-body">
                    <!-- Changes List -->
                    <div class="changes-list" v-if="filteredChangelog && filteredChangelog[versionKey] && Array.isArray(filteredChangelog[versionKey])">
                      <div
                        v-for="(change, changeIndex) in filteredChangelog[versionKey]"
                        :key="change.id || changeIndex"
                        class="change-item"
                        :class="{ 'mb-3': changeIndex < filteredChangelog[versionKey].length - 1 }"
                      >
                        <div class="d-flex align-items-start">
                          <i class="mdi mdi-circle-small text-primary me-2"></i>
                          <div class="flex-grow-1 d-flex align-items-center gap-3 flex-wrap">
                            <p class="mb-0 fw-medium">{{ (change && change.change) || 'No description' }}</p>
                            <a
                              v-if="change && change.ticket && menuToArray.includes('button.changelog.jiraticketurl')"
                              :href="`https://azerion-sports.atlassian.net/jira/software/c/projects/AZS/boards/45/backlog?selectedIssue=${change.ticket}`"
                              target="_blank"
                              rel="noopener noreferrer"
                              class="badge bg-info text-decoration-none ticket-link"
                            >
                              {{ change.ticket }}
                            </a>
                            <a v-else href="javascript:void(0);" class="badge bg-info text-decoration-none ticket-link">{{ change.ticket }}</a>
                            <div class="ms-auto d-flex gap-2">
                              <a
                                  v-if="menuToArray.includes('button.changelog.editchangelog')"
                                  href="javascript:void(0);"
                                  class="px-2 text-primary"
                                  title="Edit Changelog"
                                  @click="openEditModal(change)"
                                >
                                <i class="uil uil-pen font-size-18"></i>
                              </a>
                              <a v-if="menuToArray.includes('button.changelog.deletechangelog')"
                                href="javascript:void(0);"
                                class="px-2 text-danger"
                                title="Delete Changelog"
                                @click="deleteChangelog(change)"

                                >
                                <i class="uil uil-trash font-size-18"></i>
                              </a>
                            </div>
                          </div>
                        </div>
                        <hr v-if="changeIndex < filteredChangelog[versionKey].length - 1" class="my-3">
            </div>
                </div>
              </div>
              </div>
            </div>
          </div>

            <!-- Empty State -->
            <div v-else-if="!error && !isBusy && sortedVersions.length === 0" class="text-center py-5">
              <i class="uil uil-inbox display-4 text-muted"></i>
              <p class="mt-3 text-muted">No changelog entries available</p>
            </div>
    </div>
    <modalAddEditChangelog ref="modalAddEditChangelog" @onRefresh="loadChangelog" />
  </Layout>
</template>

<style scoped>
.ticket-link {
  cursor: pointer;
}

</style>

